import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  getCarHouseAdmins,
  addCarHouseAdmin,
  editCarHouseAdmin,
  removeCarHouseAdmin,
} from '../store/actions/carHouseAdmin-actions';

import CardItem from '../components/UI/CardItem/CardItem';
import ModalForm from '../components/UI/Modal/Modal-form/ModalForm';
import Spinner from '../components/UI/Spinner/Spinner';
import CarHouseUsers from '../components/CarHouse/CarHouseUsers';
import PageHeader from '../components/UI/PageHeader/PageHeader';
import { message, Modal, Select, Card, Tabs, Row, Col } from 'antd';
import {
  FileTextOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';

import { fetchLogos } from '../store/actions/logos-actions';
import {
  fetchCarHouseLogos,
  addLogoToCarHouse,
  removeCarHouseLogo,
} from '../store/actions/carHouseLogos-actions';

import { getCarHouseById } from '../lib/carHouses';

import SettingsModal from '../components/CarHouse/SettingsModal';

import { List, Divider, Button } from 'antd';
const { TabPane } = Tabs;

const CarHouse = () => {
  const { t } = useTranslation(); 
  const { confirm } = Modal;
  const { isLoading, carHouseAdmins } = useSelector(
    (state) => state.carHouseAdmin
  );
  const { logos } = useSelector((state) => state.logos);
  const { carHouseLogos } = useSelector((state) => state.carHouseLogos);

  const { carhouseId } = useParams();
  const dispatch = useDispatch();
  const [selectedCarHouseAdmin, setSelectedCarHouseAdmin] = useState(null);

  const [selectedLogo, setSelectedLogo] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [openLogoModal, setOpenLogoModal] = useState(false);

  const [carHouse, setCarHouse] = useState(null);
  const [selectedCarHouseUser, setSelectedCarHouseUser] = useState(null);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);

  const showConfirmDeleteLogo = (logo) => {
    confirm({
      title: t('carHouse.areYouSureDeleteLogo'),
      icon: (
        <ExclamationCircleOutlined
          style={{ color: 'var(--ant-error-color)' }}
        />
      ),
      content: (
        <div style={{ textAlign: 'center' }}>
          <img
            src={logo.path}
            alt={logo.name}
            style={{
              width: '100px',
              height: '100px',
              objectFit: 'contain',
              marginBottom: '10px',
            }}
          />
        </div>
      ),
      okType: 'danger',
      okText: t('common.delete'),
      cancelText: t('common.cancel'),
      maskClosable: true,
      onOk() {
        removeCarHouseLogoHandler(logo.id);
      },
      onCancel() {},
    });
  };

  useEffect(() => {
    const controler = new AbortController();
    const { signal } = controler;

    const fetchData = async () => {
      dispatch(
        getCarHouseAdmins({ carHouseId: carhouseId, options: { signal } })
      );
      dispatch(fetchLogos());
      dispatch(
        fetchCarHouseLogos({ carHouseId: carhouseId, options: { signal } })
      );
      getCarHouseById(carhouseId).then((res) => {
        setCarHouse(res.data);
      });
    };

    fetchData(signal);

    return () => controler.abort();
  }, [dispatch, carhouseId]);

  const onAddAdmin = () => {
    setSelectedCarHouseAdmin(null);
    setOpenForm(true);
  };

  const onEditAdmin = (carHouseAdmin) => {
    setSelectedCarHouseAdmin(carHouseAdmin);
    setOpenForm(true);
  };

  const deleteCarHouses = async (id) => {
    try {
      await dispatch(
        removeCarHouseAdmin({ carHouseId: carhouseId, carHouseAdminId: id })
      );
      message.success(t('carHouse.successRemoveAdmin'));
    } catch (err) {}
  };

  const addCarHouseAdminHandler = async (body) => {
    try {
      const resultAction = await dispatch(
        addCarHouseAdmin({ carHouseId: carhouseId, body })
      );
      if (addCarHouseAdmin.fulfilled.match(resultAction)) {
        message.success(t('carHouse.successAddAdmin'));
        setOpenForm(false);
      } else {
        const error = resultAction.payload || resultAction.error;
        const errorMessage = error?.description || t('common.error');
        message.error(errorMessage);
      }
    } catch (err) {
      setOpenForm(false);
    }
  };

  const editCarHouseAdminHandler = async (body) => {
    try {
      const resultAction = await dispatch(
        editCarHouseAdmin({
          carHouseId: carhouseId,
          carHouseAdminId: selectedCarHouseAdmin.id,
          body,
        })
      );

      if (editCarHouseAdmin.fulfilled.match(resultAction)) {
        message.success(t('carHouse.successAddAdmin'));
        setOpenForm(false);
      } else {
        const error = resultAction.payload || resultAction.error;
        const errorMessage = error?.description || t('common.error');
        message.error(errorMessage);
      }
    } catch (err) {
      setOpenForm(false);
    }
  };

  const assignLogoToCarHouseHandler = async () => {
    if (!selectedLogo) {
      message.error(t('common.errorAssignLogo'));
      return;
    }

    try {
      const resultAction = await dispatch(
        addLogoToCarHouse({ carHouseId: carhouseId, logoId: selectedLogo })
      );
      if (addLogoToCarHouse.fulfilled.match(resultAction)) {
        message.success(t('carHouse.successAddLogo'));
        setOpenLogoModal(false);
        dispatch(fetchCarHouseLogos({ carHouseId: carhouseId }));
      } else {
        const error = resultAction.payload || resultAction.error;
        const errorMessage = error?.description || t('common.error');
        message.error(errorMessage);
      }
    } catch (err) {
      message.error(t('common.errorAssignLogo'));
    }
  };

  const removeCarHouseLogoHandler = async (logoId) => {
    try {
      await dispatch(removeCarHouseLogo({ carHouseId: carhouseId, logoId }));
      message.success(t('carHouse.successRemoveLogo'));
    } catch (err) {
      message.error(t('common.errorRemoveLogo'));
    }
  };

  const onSettings = (carHouseUser) => {
    setSelectedCarHouseUser(carHouseUser);
    setOpenSettingsModal(true);
  };

  const onAssignLogo = () => {
    dispatch(getCarHouseAdmins({ carHouseId: carhouseId }));
  };

  const onOpenLogoModal = () => {
    setSelectedLogo(null);
    setOpenLogoModal(true);
  };

  const activeSubscription = carHouse?.subscriptions?.find(
    (subscription) => subscription.status === 'active'
  );

  const paymentType = activeSubscription
    ? activeSubscription.paymentType.toUpperCase()
    : '/';

  let content;
  isLoading
    ? (content = <Spinner />)
    : (content = (
        <div className="container-fluid p-0">
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 1,
              md: 2,
              lg: 3,
              xl: 3,
              xxl: 4,
            }}
            dataSource={carHouseAdmins}
            renderItem={(item) => (
              <CardItem
                user={item}
                onEdit={onEditAdmin}
                onDelete={deleteCarHouses}
                onSettings={onSettings}
              />
            )}
          />
        </div>
      ));

  return (
    <>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between">
          <Col>
            <h2>{carHouse?.name}</h2>
            <p>{t('carHouse.address')}: {carHouse?.address || '/'}</p>
            <p>{t('carHouse.phone')}: {carHouse?.phone || '/'}</p>
            <p>{t('carHouse.email')}: {carHouse?.supportEmails?.join(', ')}</p>
            <p>{t('carHouse.paymentType')}: {paymentType}</p>
            <p>{t('carHouse.credits')}: {carHouse?.credits || '/'}</p>
          </Col>
          <Col>
            <Link to={'subscriptions'}>
              <Button
                type="link"
                icon={<FileTextOutlined />}
                style={{ fontSize: '16px' }}
              >
                {t('carHouse.subscriptions')}
              </Button>
            </Link>
          </Col>
        </Row>
      </Card>
      <Divider></Divider>
      <Card>
        <PageHeader
          title={t('carHouse.logos')}
          onAdd={onOpenLogoModal}
          showButton
          buttonText={t('carHouse.addLogo')}
        />
        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 1,
            md: 2,
            lg: 3,
            xl: 3,
            xxl: 4,
          }}
          dataSource={carHouseLogos}
          renderItem={(logo) => (
            <div
              key={logo.id}
              style={{ textAlign: 'center', position: 'relative' }}
            >
              <img
                src={logo.path}
                alt={logo.name}
                style={{
                  width: '100px',
                  height: '100px',
                  objectFit: 'contain',
                }}
              />
              <div>{logo.name}</div>
              <DeleteOutlined
                style={{
                  position: 'absolute',
                  top: '5px',
                  right: '5px',
                  fontSize: '20px',
                  color: 'var(--ant-error-color)',
                  cursor: 'pointer',
                }}
                onClick={() => showConfirmDeleteLogo(logo)}
              />
            </div>
          )}
        />
      </Card>
      <Divider />
      <Tabs defaultActiveKey="1">
        <TabPane tab={t('carHouse.admins')} key="1">
          <PageHeader
            title={t('carHouse.admins')}
            onAdd={onAddAdmin}
            showButton
            buttonText={t('carHouse.addAdmin')}
          />
          {content}
        </TabPane>
        <TabPane tab={t('carHouse.users')} key="2">
          <CarHouseUsers />
        </TabPane>
      </Tabs>
      <Divider />
      <Modal
        title={t('carHouse.addLogoToCarHouse')}
        open={openLogoModal}
        onCancel={() => setOpenLogoModal(false)}
        onOk={assignLogoToCarHouseHandler}
      >
        <Select
          value={selectedLogo}
          placeholder={t('common.selectLogo')}
          onChange={setSelectedLogo}
          optionLabelProp="label"
          style={{ width: '100%' }}
          allowClear
          showSearch
          filterOption={(input, option) =>
            option?.label?.props?.children
              ?.toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {logos?.map((logo) => (
            <Select.Option
              key={logo.id}
              value={logo.id}
              label={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <img
                    src={logo.path}
                    alt={logo.name}
                    style={{
                      width: '30px',
                      height: '30px',
                      objectFit: 'contain',
                    }}
                  />
                  {logo.name}
                </div>
              }
            >
              <Row align="middle" gutter={16}>
                <Col>
                  <img
                    src={logo.path}
                    alt={logo.name}
                    style={{
                      width: '40px',
                      height: '40px',
                      objectFit: 'contain',
                    }}
                  />
                </Col>
                <Col style={{ fontSize: '16px' }}>{logo.name}</Col>
              </Row>
            </Select.Option>
          ))}
        </Select>
      </Modal>
      <ModalForm
        open={openForm}
        selectedItem={selectedCarHouseAdmin}
        add={addCarHouseAdminHandler}
        edit={editCarHouseAdminHandler}
        title={t('roles.admin')}
        onClose={() => setOpenForm(false)}
      />
      <SettingsModal
        visible={openSettingsModal}
        onClose={() => setOpenSettingsModal(false)}
        user={selectedCarHouseUser}
        carHouseId={carhouseId}
        onAssignLogo={onAssignLogo}
      />
    </>
  );
};

export default CarHouse;
